"use client";

import { ThumbsUp, ThumbsDown } from "lucide-react";
import { cn } from "@/src/lib/utils";
import { Button } from "@/components/ui/button";
import { ButtonGroup, ButtonGroupSeparator } from "@/components/ui/button-group";
import type { VerdictStatus } from "./types";
import { STATUS_COLOR, VERDICT_GRADIENT, useVerdictLabels } from "./meta";

interface VerdictToggleProps {
  /** Текущий выбранный статус (null — ничего не выбрано) */
  value: VerdictStatus | null;
  /** Клик по варианту. Вызывается даже при клике по уже выбранному (→ доп. коммент). */
  onSelect: (status: VerdictStatus) => void;
  disabled?: boolean;
}

/**
 * Сегмент-контрол «Честно / Не честно» на базе ButtonGroup.
 * Активная кнопка залита градиентом (белый текст/иконка),
 * неактивная — белая с цветной иконкой.
 */
export default function VerdictToggle({ value, onSelect, disabled }: VerdictToggleProps) {
  const labels = useVerdictLabels();
  return (
    <ButtonGroup className="rounded-[6px] border border-gray-200 bg-white overflow-hidden w-full md:w-auto md:shrink-0">
      <ToggleButton
        active={value === "honest"}
        disabled={disabled}
        onClick={() => onSelect("honest")}
        icon={<ThumbsUp className="size-4" />}
        label={labels.honest}
        idleColor={STATUS_COLOR.honest}
      />
      <ButtonGroupSeparator className="bg-gray-200" />
      <ToggleButton
        active={value === "dishonest"}
        disabled={disabled}
        onClick={() => onSelect("dishonest")}
        icon={<ThumbsDown className="size-4" />}
        label={labels.dishonest}
        idleColor="#9333ea"
      />
    </ButtonGroup>
  );
}

function ToggleButton({
  active,
  disabled,
  onClick,
  icon,
  label,
  idleColor,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  idleColor: string;
}) {
  return (
    <Button
      type="button"
      variant={active ? "default" : "outline"}
      disabled={disabled}
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-none border-0 shadow-none focus-visible:ring-0 flex-1 md:flex-none",
        active
          ? cn(VERDICT_GRADIENT, "text-white hover:text-white hover:opacity-95")
          : "bg-white text-foreground hover:bg-gray-50",
      )}
    >
      <span style={active ? { color: "#fff" } : { color: idleColor }} className="flex">
        {icon}
      </span>
      {label}
    </Button>
  );
}
